import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import type { Category } from "../../../redux/types/categoryTypes";
import { localDraftStore, LocalDraft } from "./drafts";
import { emptyListing, LocalPhoto, SellingValues, stepLabels } from "./types";
import { stepErrors } from "./validation";
import { useLeaveGuard } from "./useLeaveGuard";
import ToyDetailsStep from "./ToyDetailsStep";
import PhotosConditionStep from "./PhotosConditionStep";
import PricingStep from "./PricingStep";
import ShippingStep from "./ShippingStep";
import ReviewStep from "./ReviewStep";
import EarningsEstimate from "./EarningsEstimate";
import {
  DraftConfirmation,
  DraftControls,
  ListingHeader,
  WizardProgress,
} from "./WizardChrome";

interface Props {
  accountId: string;
  categories: Category[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  retryCategories: () => void;
}

export default function SellingWizard({
  accountId,
  categories,
  categoriesLoading,
  categoriesError,
  retryCategories,
}: Props) {
  const methods = useForm<SellingValues>({
    defaultValues: { ...emptyListing },
    mode: "onBlur",
    shouldUnregister: false,
  });
  const {
    control,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { isDirty },
  } = methods;
  const values = useWatch({ control }) as SellingValues;
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const photoRef = useRef(photos);
  photoRef.current = photos;
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [savedDraft, setSavedDraft] = useState<LocalDraft | null>(null);
  const [notice, setNotice] = useState("");
  const [draftError, setDraftError] = useState("");
  const [missingPhotos, setMissingPhotos] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState<"discard" | "resume" | null>(
    null,
  );
  const [validationNotice, setValidationNotice] = useState("");
  const heading = useRef<HTMLDivElement>(null);
  useLeaveGuard(isDirty || photos.length > 0 || photoBusy);

  useEffect(() => {
    try {
      setSavedDraft(localDraftStore.load(accountId));
    } catch {
      setDraftError(
        "This device's draft couldn't be read. You can start a new listing or discard the local draft.",
      );
    }
  }, [accountId]);
  useEffect(
    () => () => {
      photoRef.current.forEach((photo) =>
        URL.revokeObjectURL(photo.previewUrl),
      );
    },
    [],
  );
  useEffect(() => {
    heading.current?.focus();
  }, [step]);

  const changePhotos = (next: LocalPhoto[]) => {
    setPhotos(next);
    if (next.length) setPhotoError("");
    setNotice("");
  };
  const goTo = (next: number) => {
    setValidationNotice("");
    setStep(next);
  };
  const validate = (index: number) => {
    const errors = stepErrors(index, getValues(), categories);
    errors.forEach(({ name, message }) =>
      setError(name, { type: "validate", message }),
    );
    const photoInvalid = index === 1 && !photos.length;
    if (photoInvalid) setPhotoError("Add at least one photo of your toy.");
    return { errors, valid: !errors.length && !photoInvalid };
  };
  const next = (event: FormEvent) => {
    event.preventDefault();
    if (photoBusy || step === 4) return;
    clearErrors();
    const result = validate(step);
    if (!result.valid) {
      setValidationNotice("Check the highlighted fields before continuing.");
      if (result.errors[0]) methods.setFocus(result.errors[0].name);
      return;
    }
    // Revalidate every section before review, including previously unmounted fields.
    if (step === 3) {
      for (let index = 0; index < 4; index++) {
        if (!validate(index).valid) {
          setStep(index);
          setValidationNotice(
            "Check this section before reviewing your listing.",
          );
          return;
        }
      }
    }
    setValidationNotice("");
    setStep(step + 1);
    setFurthest(Math.max(furthest, step + 1));
  };
  const save = () => {
    const draft: LocalDraft = {
      version: 1,
      accountId,
      savedAt: new Date().toISOString(),
      step,
      values: getValues(),
      photoNames: photos.length
        ? photos.map((photo) => photo.name)
        : missingPhotos,
    };
    try {
      localDraftStore.save(draft);
      setSavedDraft(draft);
      reset(draft.values);
      setDraftError("");
      setNotice(
        "Draft text saved on this device for this account. Photos are not saved; reselect them after refreshing.",
      );
    } catch {
      setDraftError(
        "Your draft couldn't be saved on this device. Your entries are still here. Check browser storage permissions or free space.",
      );
    }
  };
  const resume = () => {
    if (!savedDraft) return;
    photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    setPhotos([]);
    reset(savedDraft.values);
    setMissingPhotos(savedDraft.photoNames);
    setStep(savedDraft.step);
    setFurthest(savedDraft.step);
    setPhotoError("");
    setNotice(
      "Draft resumed. Review your saved details, then continue where you left off.",
    );
    setConfirmation(null);
  };
  const discard = () => {
    try {
      localDraftStore.discard(accountId);
      photos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
      setPhotos([]);
      reset({ ...emptyListing });
      setSavedDraft(null);
      setMissingPhotos([]);
      setStep(0);
      setFurthest(0);
      setPhotoError("");
      setDraftError("");
      setValidationNotice("");
      setNotice(
        "Local draft discarded. You're ready to start a new toy listing.",
      );
      setConfirmation(null);
    } catch {
      setDraftError(
        "The local draft couldn't be discarded. Your entries have been kept.",
      );
      setConfirmation(null);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#f5f7f9",
        minHeight: "calc(100vh - 60px)",
        pb: { xs: 5, sm: 6 },
        overflowWrap: "anywhere",
        "& .MuiButton-root": { textTransform: "none", minHeight: 44 },
        "& .MuiButton-textPrimary": { color: "#9c431c" },
        "@media (prefers-reduced-motion: reduce)": {
          "& *, & *::before, & *::after": {
            animation: "none !important",
            transition: "none !important",
            scrollBehavior: "auto !important",
          },
        },
      }}
    >
      <ListingHeader />
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <DraftControls
          savedDraft={savedDraft}
          photoBusy={photoBusy}
          hasPhotos={!!photos.length}
          isDirty={isDirty}
          notice={notice}
          draftError={draftError}
          missingPhotos={missingPhotos}
          save={save}
          resume={resume}
          setConfirmation={setConfirmation}
          setNotice={setNotice}
          setDraftError={setDraftError}
        />
        <WizardProgress
          step={step}
          furthest={furthest}
          values={values}
          categories={categories}
          hasPhotos={!!photos.length}
          photoBusy={photoBusy}
          goTo={goTo}
        />
        <FormProvider {...methods}>
          <Box
            component="form"
            noValidate
            onSubmit={next}
            sx={{
              "& .MuiButton-root": {
                textTransform: "none",
                borderRadius: 20,
                minHeight: 44,
              },
              "& .MuiButton-outlinedPrimary, & .MuiButton-textPrimary": {
                color: "#9c431c",
              },
              "& .MuiButton-containedPrimary": {
                bgcolor: "#aa4d24",
                color: "white",
                "&:hover": { bgcolor: "#8c3c1a" },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "action.disabled",
                },
              },
              "& .Mui-focusVisible": {
                outline: "3px solid #a94d24",
                outlineOffset: 3,
              },
            }}
          >
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{ borderRadius: 4, p: { xs: 2, sm: 3.5 } }}
                >
                  <Box
                    ref={heading}
                    tabIndex={-1}
                    aria-label={`${stepLabels[step]} section`}
                    sx={{
                      outline: "none",
                      "&:focus-visible": {
                        outline: "2px solid #a94d24",
                        outlineOffset: 6,
                      },
                    }}
                  >
                    {step === 0 && (
                      <ToyDetailsStep
                        categories={categories}
                        loading={categoriesLoading}
                        error={categoriesError}
                        retry={retryCategories}
                      />
                    )}
                    {step === 1 && (
                      <PhotosConditionStep
                        photos={photos}
                        onPhotos={changePhotos}
                        onBusy={setPhotoBusy}
                        photoError={photoError}
                      />
                    )}
                    {step === 2 && <PricingStep />}
                    {step === 3 && <ShippingStep />}
                    {step === 4 && (
                      <ReviewStep
                        categories={categories}
                        photos={photos}
                        edit={goTo}
                      />
                    )}
                  </Box>
                  {validationNotice && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {validationNotice}
                    </Alert>
                  )}
                  <Divider sx={{ my: 3 }} />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Button
                      type="button"
                      color="secondary"
                      startIcon={<ArrowBackRounded />}
                      disabled={step === 0 || photoBusy}
                      onClick={() => goTo(step - 1)}
                    >
                      Back
                    </Button>
                    {step < 4 ? (
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          photoBusy ||
                          (step === 0 &&
                            (categoriesLoading ||
                              !!categoriesError ||
                              !categories.length))
                        }
                        endIcon={<ArrowForwardRounded />}
                      >
                        {step === 3 ? "Review Listing" : "Continue"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="contained"
                        disabled
                        startIcon={<LockOutlined />}
                      >
                        List Your Toy
                      </Button>
                    )}
                  </Stack>
                  {step === 4 && (
                    <Typography
                      variant="caption"
                      display="block"
                      textAlign="right"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Publishing isn't connected yet. Save a local draft to keep
                      your text.
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <Stack spacing={2} sx={{ position: { md: "sticky" }, top: 84 }}>
                  <Paper
                    variant="outlined"
                    sx={{ borderRadius: 4, p: { xs: 2, sm: 3 } }}
                  >
                    <EarningsEstimate price={values.price} />
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{ borderRadius: 4, p: 2.5, bgcolor: "#fffaf5" }}
                  >
                    <Chip
                      label="A little care goes a long way"
                      size="small"
                      sx={{
                        mb: 1.5,
                        color: "#8c3e1c",
                        bgcolor: "#fce8d8",
                        maxWidth: "100%",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Photograph the real toy, include its accessories, and be
                      open about wear. The details matter to its next collector.
                    </Typography>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      </Container>
      <DraftConfirmation
        confirmation={confirmation}
        setConfirmation={setConfirmation}
        discard={discard}
        resume={resume}
      />
    </Box>
  );
}
